import React from 'react';
import PropTypes from 'prop-types';
import Container from './Container';
import Item from './Item';
import { toggleSelectedItem } from './util/toggleSelectedItem';
import { pageBuilder } from './util/pageBuilder';

/* <Pagination />
---------------------------------------------------------------------------- */
/**
 * A row of interactive elements that can be toggled active/inactive.
 * By default, only one is allowed to be active at a given time.
 */
export default class Pagination extends React.Component {
  static propTypes = {
    /** Selected state changed */
    onChange: PropTypes.func.isRequired,
    /** aria-label or accessibilityLabel attribute */
    accessibilityLabel: PropTypes.string,
    /** aria-label or accessibilityLabel attribute for previous button */
    accessibilityLabelPrevButton: PropTypes.string,
    /** aria-label or accessibilityLabel attribute for next button */
    accessibilityLabelNextButton: PropTypes.string,
    /** aria-hidden or accessible attribute */
    accessible: PropTypes.bool,
    /** Initially selected index */
    defaultSelected: PropTypes.number,
    /** Text direction */
    isRtl: PropTypes.bool,
    /** Interaction event handler */
    onPress: PropTypes.func,
    /** Selected index */
    selected: PropTypes.number,
    /** For e2e tests (maps to `data-cvent-id`) */
    testID: PropTypes.string,
    /** Total number of pages */
    totalPages: PropTypes.number,
    /** Hides the page numbers to prevent skipping pages by clicking them */
    hideNumbers: PropTypes.bool
  };

  static defaultProps = {
    accessibilityLabel: '',
    accessible: true,
    isRtl: false,
    selected: null,
    defaultSelected: 1,
    totalPages: 1,
    accessibilityLabelPrevButton: 'Previous Page',
    accessibilityLabelNextButton: 'Next Page',
    hideNumbers: false
  };

  state = {
    selected: null,
    controlled: null
  };

  static getDerivedStateFromProps({ defaultSelected, selected }, state) {
    return {
      ...state,
      selected: state.selected !== null ? state.selected : defaultSelected,
      controlled: selected !== null
    };
  }

  /**
   * Take in a list item index and, as a side-effect, update state to
   * toggle active state on or off
   * @param {number} index List item index
   */
  toggleItem = index => {
    let selected;
    if (this.state.controlled) {
      selected = toggleSelectedItem(index, this.props.selected);
    } else {
      selected = toggleSelectedItem(index, this.state.selected);
      this.setState({ selected });
    }
    this.props.onChange(selected);
  };

  render() {
    const {
      accessibilityLabel,
      accessible,
      isRtl,
      testID,
      totalPages,
      hideNumbers
    } = this.props;
    const { selected } = this.state.controlled ? this.props : this.state;

    return (
      <Container
        accessible={accessible}
        accessibilityLabel={accessibilityLabel}
        isRtl={isRtl}
        testID={testID}
      >
        {this.renderIconButton('left', selected, totalPages)}
        {!hideNumbers && this.renderItems(selected, totalPages)}
        {this.renderIconButton('right', selected, totalPages)}
      </Container>
    );
  }

  renderItems(selected, totalPages) {
    let pageChildrenArray = pageBuilder(selected, totalPages);

    return React.Children.map(pageChildrenArray, element => {
      return React.cloneElement(element, {
        isRtl: this.props.isRtl,
        accessible: this.props.accessible,
        accessibilityLabel: this.props.accessibilityLabel,
        onPress: e => {
          this.toggleItem(Number(element.props.text));
          if (element.props.onPress) {
            element.props.onPress(e);
          }
        }
      });
    });
  }

  renderIconButton(direction, selected, totalPages) {
    return (
      <Item
        isRtl={this.props.isRtl}
        accessibilityLabel={
          direction === 'left'
            ? this.props.accessibilityLabelPrevButton
            : this.props.accessibilityLabelNextButton
        }
        icon={direction}
        disabled={
          (selected === 1 && direction === 'left') ||
          (selected === totalPages && direction === 'right')
        }
        onPress={e => {
          this.toggleItem(direction === 'left' ? selected - 1 : selected + 1);
          if (this.props.onPress) {
            this.props.onPress(e);
          }
        }}
      />
    );
  }
}
